import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useStorage } from '@vueuse/core'
import { useRouter } from 'vue-router'

import type { ChangePasswordPayload, SignUpPayload } from '@/api/modules/auth/types'
import { AuthApi } from '@/api/modules/auth'
import { Routes } from '@/config/router/routes'
import { useUserStore } from './user-store'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const userStore = useUserStore()
  const { profile } = storeToRefs(userStore)

  const isAuthenticated = computed(() => {
    return token.value && userId.value !== undefined
  })

  const token = useStorage<string | null>('auth.token', null)
  const userId = useStorage<string | null>('auth.user_id', null)

  const loading = ref<boolean>(false)

  const currentResetId = ref('')
  const currentResetToken = ref('')
  const currentEmail = ref('')

  const checkAuth = async () => {
    if (isAuthenticated.value && !profile.value?.email) {
      await userStore.userInfo()
    }
  }

  const clearAuthData = () => {
    token.value = null
    userId.value = null
  }

  const setAuthData = async (t: string) => {
    token.value = t
    userId.value = '1'
    try {
      await checkAuth()
      router.push({ name: Routes.Chat })
    } catch {
      logout()
    }
  }

  const login = async (email: string, password: string) => {
    loading.value = true
    try {
      const res = await AuthApi.loginUserRequest({ email, password })
      token.value = res.token
      userId.value = '1'
    } finally {
      loading.value = false
    }
  }

  const getCode = async (email: string) => {
    loading.value = true
    try {
      const res = await AuthApi.loginUserRequestNew({ email })
      token.value = res.token
      userId.value = '1'
    } finally {
      loading.value = false
    }
  }

  const verifyCode = async (email: string, code: string) => {
    loading.value = true
    try {
      const res = await AuthApi.verifyCode(email, code)
      token.value = res.token
      userId.value = '1'

      router.push({ name: Routes.Chat })
    } finally {
      loading.value = false
    }
  }

  const exchangeToken = async (authToken: string) => {
    try {
      const res = await AuthApi.exchangeTokenRequest(authToken)
      token.value = res.token
      userId.value = '1'

      router.push({ name: Routes.Chat })
    } catch {
      logout()
    }
  }

  const requestPasswordReset = async (email: string): Promise<any> => {
    loading.value = true
    try {
      currentEmail.value = email
      const res = await AuthApi.requestPasswordResetRequest(email)
      currentResetId.value = res.uniqueId
      return res
    } finally {
      loading.value = false
    }
  }

  const validateResetToken = async (token: string, id: string): Promise<any> => {
    loading.value = true
    try {
      currentResetId.value = id
      currentResetToken.value = token
      return await await AuthApi.validateResetTokenRequest(token, id)
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (payload: ChangePasswordPayload) => {
    loading.value = true
    try {
      await AuthApi.changePasswordRequest(payload)
      logout()
    } finally {
      loading.value = false
    }
  }

  const resetPassword = async (password: string, password_confirm: string): Promise<any> => {
    loading.value = true
    try {
      return await AuthApi.resetPasswordRequest(
        currentResetToken.value,
        currentResetId.value,
        password,
        password_confirm,
      )
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    token.value = null
    userId.value = undefined

    router.push({ name: Routes.Login })
  }

  const signUp = async (payload: SignUpPayload) => {
    loading.value = true
    try {
      const res = await AuthApi.signUpRequest(payload)
      token.value = res.token
      userId.value = '1'
    } finally {
      loading.value = false
    }
  }

  const signUpNew = async (payload: SignUpPayload) => {
    loading.value = true
    try {
      await AuthApi.signUpRequestNew(payload)
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    token,
    currentEmail,
    currentResetId,

    userId,

    isAuthenticated,

    clearAuthData,
    checkAuth,
    setAuthData,
    exchangeToken,
    login,
    getCode,
    logout,
    signUp,
    signUpNew,
    requestPasswordReset,
    validateResetToken,
    changePassword,
    verifyCode,
    resetPassword,
  }
})
